/* eslint-disable no-restricted-imports -- the wrapper module is the moment island during the shadow window; raw moment access is required to delegate the Moment side of the comparison. */
import moment, {
  type Moment,
  type MomentFormatSpecification,
  type MomentInput,
} from 'moment';
import momentTimezone from 'moment-timezone';

import { shadowDuration } from './duration';
import { shadowParseZone } from './parse-zone';
import { dayjsApi, formatSerializer, runShadow } from './shadow-runner';
import {
  type DomainNameType,
  type SensitiveDomainType,
  type SubdomainForType,
} from './subdomains';
import { shadowTz } from './tz';

export type MomentFactoryType = {
  (
    input?: MomentInput,
    format?: MomentFormatSpecification,
    strict?: boolean
  ): Moment;
  tz: typeof momentTimezone.tz;
  parseZone: typeof moment.parseZone;
  duration: typeof moment.duration;
  utc: typeof moment.utc;
  updateLocale: typeof moment.updateLocale;
  isMoment: typeof moment.isMoment;
  now: typeof moment.now;
};

// Sensitive-area domains require a typed subdomain second argument.
// Non-sensitive domains accept the one-arg form. The TypeScript overloads
// make the wrong shape a compile error at the per-domain wrapper PR.
export function momentForDomain<D extends SensitiveDomainType>(
  domain: D,
  subdomain: SubdomainForType<D>
): MomentFactoryType;

export function momentForDomain(
  domain: Exclude<DomainNameType, SensitiveDomainType>
): MomentFactoryType;

export function momentForDomain(
  domain: DomainNameType,
  subdomain?: string
): MomentFactoryType {
  const tags = { domain, subdomain: subdomain ?? '' };

  const factory = ((
    input?: MomentInput,
    format?: MomentFormatSpecification,
    strict?: boolean
  ): Moment => {
    // `now()` construction (no input): runShadow builds the two sides
    // sequentially (computeMoment, then computeDayjs), so reading the clock
    // independently for each makes the dayjs side a few ms later than the moment
    // side — a skew that straddles ms/unit boundaries and emits benign now()
    // mismatches (WORK-6090: WORKS-32Z `toISOString`, WORKS-332 `now.diff(...)`).
    // Capture a single instant up front and hand it to both sides so they compare
    // the same "now". `inputs` keeps the original call shape for the report.
    const resolvedInput = input === undefined ? Date.now() : input;
    return runShadow({
      op: 'moment',
      ...tags,
      inputs: [input, format, strict],
      computeMoment: () => moment(resolvedInput, format, strict),
      computeDayjs: () =>
        dayjsApi.datetime(
          resolvedInput as Parameters<typeof dayjsApi.datetime>[0],
          format as Parameters<typeof dayjsApi.datetime>[1],
          strict as Parameters<typeof dayjsApi.datetime>[2]
        ),
      serialize: formatSerializer,
    });
  }) as MomentFactoryType;

  // Sensitive entry points (the four ops the scaffold instruments) route
  // through the shadow runner. Pass-through helpers (`utc`, `updateLocale`,
  // `isMoment`, `now`) mirror raw moment until a future PR adds wrappers for
  // them; `isMoment` is a pure type guard with no shadow/metric concern. The
  // dayjs equivalents either have no straightforward parity (`updateLocale`
  // mutates global state, `now` has no `dayjs.now()`) or are not exercised by
  // the rollout cohort today.
  factory.tz = shadowTz(tags) as typeof momentTimezone.tz;
  factory.parseZone = shadowParseZone(tags) as typeof moment.parseZone;
  factory.duration = shadowDuration(tags) as typeof moment.duration;
  factory.utc = moment.utc;
  factory.updateLocale = moment.updateLocale;
  factory.isMoment = moment.isMoment;
  factory.now = moment.now;

  return factory;
}
